import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  getUsers,
  searchUsers,
  suspendUser,
  unsuspendUser,
  getUserStats,
  getSystemStats,
  type StoredUser,
} from '@/lib/usersStore'
import {
  REPORT_REASONS,
  type AbuseReport,
  type ReportReason,
  type ReportStatus,
} from '@/lib/reportsStore'
import { getPhotoById } from '@/lib/photosStore'
import { moderateDeletePhoto } from '@/lib/uploadClient'
import { UPLOAD_WORKER_URL } from '@/lib/env'
import {
  useReportsQuery,
  useUpdateReportStatusMutation,
  type ReportRow,
} from '@/data/reports'

// Adapt a Supabase `reports` RPC row (with photo+drop join) to the UI's AbuseReport
// shape. The backend stores a single text `reason` column, so the structured
// reason id and free-form description are packed as "{reasonId}: {description}"
// at insert time and unpacked here.
function adaptReport(r: ReportRow): AbuseReport {
  const colonIdx = r.reason.indexOf(': ')
  const reasonId = colonIdx > 0 ? r.reason.slice(0, colonIdx) : r.reason
  const description = colonIdx > 0 ? r.reason.slice(colonIdx + 2) : undefined
  return {
    id: r.id,
    photoId: r.photo_id ?? undefined,
    dropId: r.photo?.drop?.id ?? 'unknown',
    dropName: r.photo?.drop?.name ?? 'Unknown drop',
    reason: reasonId as ReportReason,
    description,
    status: r.status as ReportStatus,
    createdAt: new Date(r.created_at),
    reviewedAt: r.resolved_at ? new Date(r.resolved_at) : undefined,
    reviewedBy: undefined,
    resolution: undefined,
    reporterSession: 'n/a',
    galleryToken: undefined,
  }
}
import {
  Search,
  Users,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  UserX,
  UserCheck,
  AlertTriangle,
  Clock,
  Building2,
  TrendingUp,
  X,
  Flag,
  Image,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  HardDrive,
  FolderOpen,
  Camera,
  Ticket,
  Copy,
  Plus,
  Loader2,
} from 'lucide-react'
import {
  useInviteCodesQuery,
  useCreateInviteCodeMutation,
  useRevokeInviteCodeMutation,
} from '@/data/inviteCodes'

type AdminTab = 'users' | 'reports' | 'invites'

export default function SuperAdminPage() {
  const { user, hasPermission } = useAuth()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<AdminTab>('users')
  const [users, setUsers] = useState<StoredUser[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [stats, setStats] = useState<ReturnType<typeof getUserStats> | null>(null)
  const [systemStats, setSystemStats] = useState<ReturnType<typeof getSystemStats> | null>(null)
  const [filterTier, setFilterTier] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showSuspendModal, setShowSuspendModal] = useState(false)
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<StoredUser | null>(null)
  const [suspendReason, setSuspendReason] = useState('')

  // Reports — live from Supabase, scoped to the caller's org by row-level
  // permissions. The query auto-refetches on mutation success.
  const reportsQuery = useReportsQuery()
  const updateReportStatusMutation = useUpdateReportStatusMutation()
  const reports = useMemo<AbuseReport[]>(
    () => (reportsQuery.data ?? []).map(adaptReport),
    [reportsQuery.data],
  )
  const pendingReportsCount = useMemo(
    () => reports.filter((r) => r.status === 'pending').length,
    [reports],
  )

  const [reportFilter, setReportFilter] = useState<string>('pending')
  const [selectedReport, setSelectedReport] = useState<AbuseReport | null>(null)
  const [showReportDetailModal, setShowReportDetailModal] = useState(false)
  const [showRemovePhotoModal, setShowRemovePhotoModal] = useState(false)

  // Load users on mount
  useEffect(() => {
    loadUsers()
    loadStats()
  }, [])

  const loadUsers = () => {
    if (searchQuery) {
      setUsers(searchUsers(searchQuery))
    } else {
      setUsers(getUsers())
    }
  }

  const loadStats = () => {
    setStats(getUserStats())
    setSystemStats(getSystemStats())
  }

  // Re-search when query changes
  useEffect(() => {
    loadUsers()
  }, [searchQuery])

  // Filter users
  const filteredUsers = users.filter(u => {
    if (filterTier !== 'all' && u.tier !== filterTier) return false
    if (filterStatus === 'active' && u.suspended) return false
    if (filterStatus === 'suspended' && !u.suspended) return false
    return true
  })

  // Filter reports
  const filteredReports = reports.filter(r => {
    if (reportFilter === 'pending' && r.status !== 'pending') return false
    if (reportFilter === 'resolved' && r.status !== 'resolved') return false
    if (reportFilter === 'dismissed' && r.status !== 'dismissed') return false
    return true
  })

  // Get reason label
  const getReasonLabel = (reasonId: string) => {
    const reason = REPORT_REASONS.find(r => r.id === reasonId)
    return reason?.label || reasonId
  }

  // Open report detail modal
  const handleReviewReport = (report: AbuseReport) => {
    setSelectedReport(report)
    setShowReportDetailModal(true)
  }

  // Remove photo and resolve report. Calls the upload-handler's
  // /moderate-delete-photo endpoint which authorizes the caller as
  // owner/admin of the photo's org, deletes R2 objects + database row, then
  // flips the report status to resolved.
  const handleRemovePhoto = async () => {
    if (!selectedReport || !user) return

    if (selectedReport.photoId) {
      if (!UPLOAD_WORKER_URL) {
        toast({
          variant: 'destructive',
          title: 'Upload worker not configured',
          description: 'VITE_UPLOAD_WORKER_URL is missing; cannot remove photo.',
        })
        return
      }
      try {
        await moderateDeletePhoto(UPLOAD_WORKER_URL, selectedReport.photoId)
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Failed to remove photo',
          description: err instanceof Error ? err.message : 'An error occurred.',
        })
        return
      }
    }

    try {
      await updateReportStatusMutation.mutateAsync({
        id: selectedReport.id,
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
      })
      toast({
        title: 'Photo removed',
        description: 'The photo has been removed and the report resolved.',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Photo removed but report status update failed',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }

    setShowRemovePhotoModal(false)
    setShowReportDetailModal(false)
    setSelectedReport(null)
  }

  // Dismiss report (no action taken).
  const handleDismissReport = async () => {
    if (!selectedReport || !user) return

    try {
      await updateReportStatusMutation.mutateAsync({
        id: selectedReport.id,
        status: 'dismissed',
        resolvedAt: new Date().toISOString(),
      })
      toast({
        title: 'Report dismissed',
        description: 'The report has been dismissed. No action was taken.',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to dismiss report',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }

    setShowReportDetailModal(false)
    setSelectedReport(null)
  }

  // Check permission
  if (!hasPermission('super_admin')) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <ShieldX className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            You don't have permission to access the Super Admin panel.
          </p>
        </div>
      </div>
    )
  }

  const handleSuspendClick = (targetUser: StoredUser) => {
    setSelectedUser(targetUser)
    setSuspendReason('')
    setShowSuspendModal(true)
  }

  const handleUnsuspendClick = (targetUser: StoredUser) => {
    setSelectedUser(targetUser)
    setShowUnsuspendModal(true)
  }

  const handleConfirmSuspend = () => {
    if (!selectedUser || !user) return

    const result = suspendUser(selectedUser.id, user.email, suspendReason || undefined)
    if (result) {
      toast({
        title: 'User suspended',
        description: `${selectedUser.name} (${selectedUser.email}) has been suspended.`,
      })
      loadUsers()
      loadStats()
    } else {
      toast({
        title: 'Error',
        description: 'Failed to suspend user.',
        variant: 'destructive',
      })
    }
    setShowSuspendModal(false)
    setSelectedUser(null)
    setSuspendReason('')
  }

  const handleConfirmUnsuspend = () => {
    if (!selectedUser) return

    const result = unsuspendUser(selectedUser.id)
    if (result) {
      toast({
        title: 'User unsuspended',
        description: `${selectedUser.name} (${selectedUser.email}) has been unsuspended.`,
      })
      loadUsers()
      loadStats()
    } else {
      toast({
        title: 'Error',
        description: 'Failed to unsuspend user.',
        variant: 'destructive',
      })
    }
    setShowUnsuspendModal(false)
    setSelectedUser(null)
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getTierBadgeClass = (tier: string) => {
    switch (tier) {
      case 'business':
        return 'bg-violet-500/10 text-violet-500'
      case 'pro':
        return 'bg-blue-500/10 text-blue-500'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            Super Admin Panel
          </h1>
          <p className="text-muted-foreground mt-1">Manage users and platform operations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <Users className="h-4 w-4" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'reports'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <Flag className="h-4 w-4" />
            Reports / Moderation
            {pendingReportsCount > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-destructive/15 text-destructive">
                {pendingReportsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'invites'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <Ticket className="h-4 w-4" />
            Invite Codes
          </button>
        </nav>
      </div>

      {/* Invite Codes Tab Content */}
      {activeTab === 'invites' && <InviteCodesTab />}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
      {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/15 rounded-lg">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                </div>
            </div>
          </div>
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/15 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Today</p>
                  <p className="text-2xl font-bold text-foreground">{stats.activeToday}</p>
                </div>
            </div>
          </div>
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-destructive/15 rounded-lg">
                  <ShieldAlert className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Suspended</p>
                  <p className="text-2xl font-bold text-foreground">{stats.suspended}</p>
                </div>
            </div>
          </div>
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">By Tier</p>
                  <p className="text-sm font-medium text-foreground">
                    {stats.byTier.free}F / {stats.byTier.pro}P / {stats.byTier.business}B
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Stats Cards */}
        {systemStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/10 rounded-lg">
                  <FolderOpen className="h-5 w-5 text-indigo-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Drops</p>
                  <p className="text-2xl font-bold text-foreground">{systemStats.totalDrops}</p>
                </div>
              </div>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-500/10 rounded-lg">
                  <Camera className="h-5 w-5 text-pink-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Photos</p>
                  <p className="text-2xl font-bold text-foreground">{systemStats.totalPhotos.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Users className="h-5 w-5 text-cyan-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Contributors</p>
                  <p className="text-2xl font-bold text-foreground">{systemStats.totalContributors}</p>
                </div>
              </div>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <HardDrive className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Storage Used</p>
                  <p className="text-2xl font-bold text-foreground">{systemStats.storageUsedFormatted}</p>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users by name, email, or organization..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {/* Tier Filter */}
          <select
            value={filterTier}
            onChange={e => setFilterTier(e.target.value)}
            className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            <option value="all">All Tiers</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="business">Business</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card text-card-foreground rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/50">
          <h2 className="font-semibold text-foreground">
            Users ({filteredUsers.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Organization</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Tier</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Stats</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Login</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
                {filteredUsers.map(u => (
                  <tr
                    key={u.id}
                    className={`hover:bg-muted/50 ${u.suspended ? 'bg-destructive/5' : ''}`}
                  >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        {u.avatarUrl ? (
                          <img
                            src={u.avatarUrl}
                            alt={u.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-muted-foreground">
                            {u.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-foreground">{u.organizationName}</p>
                    <p className="text-xs text-muted-foreground">Since {formatDate(u.createdAt)}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getTierBadgeClass(
                        u.tier
                      )}`}
                    >
                      {u.tier}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-muted-foreground">
                      <p>{u.dropsCount} drops</p>
                      <p>{u.photosCount} photos</p>
                    </div>
                  </td>
                    <td className="py-3 px-4">
                      {u.suspended ? (
                        <div className="flex items-center gap-1">
                          <ShieldAlert className="h-4 w-4 text-destructive" />
                          <span className="text-sm font-medium text-destructive">Suspended</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <ShieldCheck className="h-4 w-4 text-secondary" />
                          <span className="text-sm font-medium text-secondary">Active</span>
                        </div>
                      )}
                    {u.suspended && u.suspendedAt && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(u.suspendedAt)}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {formatDate(u.lastLoginAt)}
                    </div>
                  </td>
                    <td className="py-3 px-4 text-right">
                      {u.suspended ? (
                        <button
                          onClick={() => handleUnsuspendClick(u)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-secondary bg-secondary/10 rounded-lg hover:bg-secondary/15 transition-colors"
                        >
                          <UserCheck className="h-4 w-4" />
                          Unsuspend
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSuspendClick(u)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-destructive bg-destructive/10 rounded-lg hover:bg-destructive/15 transition-colors"
                        >
                          <UserX className="h-4 w-4" />
                          Suspend
                        </button>
                      )}
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    No users found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Reports Tab Content */}
      {activeTab === 'reports' && (
        <>
          {/* Reports Filter */}
          <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-foreground">Filter by status:</label>
              <select
                value={reportFilter}
                onChange={e => setReportFilter(e.target.value)}
                className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                <option value="all">All Reports</option>
                <option value="pending">Pending ({reports.filter(r => r.status === 'pending').length})</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
          </div>

          {/* Reports List */}
          <div className="bg-card text-card-foreground rounded-lg border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted/50">
              <h2 className="font-semibold text-foreground">
                Reports ({filteredReports.length})
              </h2>
            </div>
            <div className="divide-y divide-border">
              {filteredReports.length === 0 ? (
                <div className="p-8 text-center">
                  <Flag className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No reports found</p>
                  {reportFilter === 'pending' && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Great! There are no pending reports to review.
                    </p>
                  )}
                </div>
              ) : (
                  filteredReports.map(report => (
                    <div
                      key={report.id}
                      className={`p-4 hover:bg-muted/50 ${
                        report.status === 'pending' ? 'bg-accent/5' : ''
                      }`}
                    >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {/* Photo thumbnail or placeholder */}
                        <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {report.photoId ? (
                            (() => {
                              const photo = getPhotoById(report.photoId)
                              return photo?.thumbnailUrl ? (
                                <img
                                  src={photo.thumbnailUrl}
                                  alt="Reported photo"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Image className="h-6 w-6 text-muted-foreground" />
                              )
                            })()
                          ) : (
                            <Image className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>

                        {/* Report details */}
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                report.status === 'pending'
                                  ? 'bg-accent/15 text-accent'
                                  : report.status === 'resolved'
                                  ? 'bg-secondary/15 text-secondary'
                                  : 'bg-muted text-muted-foreground'
                              }`}>
                              {report.status === 'pending' && <Clock className="h-3 w-3" />}
                              {report.status === 'resolved' && <CheckCircle className="h-3 w-3" />}
                              {report.status === 'dismissed' && <XCircle className="h-3 w-3" />}
                              {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {report.createdAt.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>

                          <p className="font-medium text-foreground">
                            {getReasonLabel(report.reason)}
                          </p>

                          <p className="text-sm text-muted-foreground">
                            Drop: <span className="font-medium">{report.dropName}</span>
                          </p>

                          {report.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {report.description}
                            </p>
                          )}

                          {report.reviewedAt && (
                            <p className="text-xs text-muted-foreground">
                              Reviewed {formatDate(report.reviewedAt.toISOString())}
                              {report.reviewedBy && ` by ${report.reviewedBy}`}
                              {report.resolution && ` — ${report.resolution}`}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleReviewReport(report)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          Review
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Suspend User</h3>
              <button
                onClick={() => setShowSuspendModal(false)}
                className="p-1 hover:bg-muted rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-destructive">
                      Are you sure you want to suspend this user?
                    </p>
                    <p className="text-muted-foreground mt-1">
                      They will not be able to log in until unsuspended.
                    </p>
                  </div>
                </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.organizationName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Reason for suspension (optional)
                </label>
                <textarea
                  value={suspendReason}
                  onChange={e => setSuspendReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="Enter reason for suspension..."
                />
              </div>
            </div>
              <div className="px-6 py-4 bg-muted/50 flex justify-end gap-3">
              <button
                onClick={() => setShowSuspendModal(false)}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                Cancel
              </button>
                <button
                  onClick={handleConfirmSuspend}
                  className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded-lg transition-colors"
                >
                  Suspend User
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsuspend Modal */}
      {showUnsuspendModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Unsuspend User</h3>
              <button
                onClick={() => setShowUnsuspendModal(false)}
                className="p-1 hover:bg-muted rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-start gap-3 p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <ShieldCheck className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground">
                      Unsuspend this user?
                    </p>
                    <p className="text-muted-foreground mt-1">
                      They will be able to log in and access their account again.
                    </p>
                  </div>
                </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.organizationName}</p>
                {selectedUser.suspendedReason && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground">Suspension reason:</p>
                    <p className="text-sm text-foreground">{selectedUser.suspendedReason}</p>
                  </div>
                )}
                {selectedUser.suspendedAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Suspended on {formatDate(selectedUser.suspendedAt)}
                    {selectedUser.suspendedBy && ` by ${selectedUser.suspendedBy}`}
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-muted/50 flex justify-end gap-3">
              <button
                onClick={() => setShowUnsuspendModal(false)}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUnsuspend}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                Unsuspend User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Detail Modal */}
      {showReportDetailModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-xl shadow-xl max-w-2xl w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Flag className="h-5 w-5 text-red-500" />
                Review Report
              </h3>
              <button
                onClick={() => {
                  setShowReportDetailModal(false)
                  setSelectedReport(null)
                }}
                className="p-1 hover:bg-muted rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              {/* Photo preview */}
              <div className="bg-muted rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                {selectedReport.photoId ? (
                  (() => {
                    const photo = getPhotoById(selectedReport.photoId)
                    return photo?.originalUrl ? (
                      <img
                        src={photo.originalUrl}
                        alt="Reported photo"
                        className="max-h-[300px] max-w-full object-contain rounded-lg"
                      />
                    ) : (
                      <div className="text-center">
                        <Image className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Photo not available</p>
                      </div>
                    )
                  })()
                ) : (
                  <div className="text-center">
                    <Image className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No specific photo reported</p>
                  </div>
                )}
              </div>

              {/* Report details */}
              <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedReport.status === 'pending'
                        ? 'bg-accent/15 text-accent'
                        : selectedReport.status === 'resolved'
                        ? 'bg-secondary/15 text-secondary'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                    </span>
                  </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Reason</p>
                    <p className="font-medium text-foreground">{getReasonLabel(selectedReport.reason)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Drop</p>
                    <p className="font-medium text-foreground">{selectedReport.dropName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Reported</p>
                    <p className="text-sm text-foreground">
                      {selectedReport.createdAt.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Photo ID</p>
                    <p className="text-sm text-foreground font-mono">
                      {selectedReport.photoId || 'N/A'}
                    </p>
                  </div>
                </div>

                {selectedReport.description && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Description</p>
                    <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">
                      {selectedReport.description}
                    </p>
                  </div>
                )}

                  {selectedReport.resolution && (
                    <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                      <p className="text-xs text-secondary uppercase tracking-wide mb-1">Resolution</p>
                      <p className="text-sm text-foreground">{selectedReport.resolution}</p>
                      {selectedReport.reviewedBy && selectedReport.reviewedAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          By {selectedReport.reviewedBy} on {formatDate(selectedReport.reviewedAt.toISOString())}
                        </p>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Actions */}
            {selectedReport.status === 'pending' && (
              <div className="px-6 py-4 bg-muted/50 flex justify-end gap-3 flex-shrink-0 border-t border-border">
                <button
                  onClick={handleDismissReport}
                  className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Dismiss Report
                </button>
                  <button
                    onClick={() => setShowRemovePhotoModal(true)}
                    className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove Photo
                  </button>
              </div>
            )}

            {selectedReport.status !== 'pending' && (
              <div className="px-6 py-4 bg-muted/50 flex justify-end gap-3 flex-shrink-0 border-t border-border">
                <button
                  onClick={() => {
                    setShowReportDetailModal(false)
                    setSelectedReport(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Remove Photo Confirmation Modal */}
      {showRemovePhotoModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-card text-card-foreground rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Confirm Removal</h3>
              <button
                onClick={() => setShowRemovePhotoModal(false)}
                className="p-1 hover:bg-muted rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-destructive">
                      Are you sure you want to remove this photo?
                    </p>
                    <p className="text-muted-foreground mt-1">
                      This action cannot be undone. The photo will be permanently deleted from the drop.
                    </p>
                  </div>
                </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Report reason:</span> {getReasonLabel(selectedReport.reason)}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Drop:</span> {selectedReport.dropName}
                </p>
                {selectedReport.photoId && (
                  <p className="text-sm text-muted-foreground font-mono mt-1">
                    Photo: {selectedReport.photoId}
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-muted/50 flex justify-end gap-3">
              <button
                onClick={() => setShowRemovePhotoModal(false)}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemovePhoto}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Remove Photo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Invite Codes: generate single-use codes that grant Pro for a configurable number of
// free months, and track redemptions.
function InviteCodesTab() {
  const { toast } = useToast()
  const codesQuery = useInviteCodesQuery()
  const createMutation = useCreateInviteCodeMutation()
  const revokeMutation = useRevokeInviteCodeMutation()

  const [months, setMonths] = useState(3)
  const [note, setNote] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const codes = codesQuery.data ?? []

  const handleGenerate = async () => {
    if (months < 1 || months > 60) {
      toast({ variant: 'destructive', title: 'Invalid duration', description: 'Months must be between 1 and 60.' })
      return
    }
    try {
      const created = await createMutation.mutateAsync({
        months,
        note: note.trim() || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      })
      setNote('')
      setExpiresAt('')
      toast({ title: 'Invite code created', description: `${created.code} — ${created.months} free month(s) of Pro.` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to create code',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  const handleCopy = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedId(id)
      window.setTimeout(() => setCopiedId(null), 1500)
    } catch {
      toast({ variant: 'destructive', title: 'Copy failed', description: 'Clipboard is unavailable.' })
    }
  }

  const handleRevoke = async (id: string) => {
    try {
      await revokeMutation.mutateAsync(id)
      toast({ title: 'Code revoked' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to revoke',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  const stateBadge = (state: string) => {
    switch (state) {
      case 'redeemed':
        return 'bg-secondary/15 text-secondary'
      case 'expired':
        return 'bg-muted text-muted-foreground'
      default:
        return 'bg-blue-500/10 text-blue-500'
    }
  }

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <div className="space-y-6">
      {/* Generate form */}
      <div className="bg-card text-card-foreground rounded-lg border border-border p-4">
        <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
          <Plus className="h-4 w-4 text-primary" />
          Generate Pro invite code
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Free months</label>
            <input
              type="number"
              min={1}
              max={60}
              value={months}
              onChange={e => setMonths(Number(e.target.value))}
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-muted-foreground mb-1">Note (who is it for?)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Jane at Acme Photo"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Code expires (optional)</label>
            <input
              type="date"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={handleGenerate} disabled={createMutation.isPending} className="gap-2">
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
            Generate code
          </Button>
        </div>
      </div>

      {/* Codes list */}
      <div className="bg-card text-card-foreground rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/50">
          <h2 className="font-semibold text-foreground">Invite codes ({codes.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Code</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Months</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Note</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Redeemed by</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {codesQuery.isLoading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!codesQuery.isLoading && codes.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    No invite codes yet. Generate one above.
                  </td>
                </tr>
              )}
              {codes.map(c => (
                <tr key={c.id} className="hover:bg-muted/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-foreground">{c.code}</span>
                      <button
                        onClick={() => handleCopy(c.code, c.id)}
                        className="p-1 rounded hover:bg-muted transition-colors"
                        title="Copy code"
                      >
                        {copiedId === c.id
                          ? <CheckCircle className="h-3.5 w-3.5 text-secondary" />
                          : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">{c.months}</td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{c.note || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${stateBadge(c.state)}`}>
                      {c.state}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {c.redeemed_at ? (
                      <div>
                        <p className="text-foreground">{c.redeemed_by_org_name || c.redeemed_by_email || 'Unknown'}</p>
                        <p className="text-xs">{formatDate(c.redeemed_at)}</p>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {c.state === 'available' && (
                      <button
                        onClick={() => handleRevoke(c.id)}
                        disabled={revokeMutation.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-destructive bg-destructive/10 rounded-lg hover:bg-destructive/15 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
